// Navigation hub model — the 21-item rail collapses to 8 hubs. Each hub is a
// group of existing routes; the rail shows the hub, and a sub-tab bar (HubTabs)
// navigates between the hub's modules. No page embedding — every route renders
// exactly as before. Module flags + roles filter individual items.

export interface HubItem {
  label: string;
  path: string;
  moduleName?: string;   // gated by the company's enabled modules
  managerOnly?: boolean;
  adminOnly?: boolean;
}

export interface Hub {
  id: string;
  label: string;
  icon: string;          // key resolved to an MUI icon in NavigationRail
  items: HubItem[];
}

// Optimistic default while /company/modules loads (or fails): every module the
// nav gates on. Shared by NavigationRail + BottomHubBar so they never diverge.
export const FALLBACK_MODULES = [
  'wellness', 'learning', 'microlearning', 'games', 'announcements', 'chat',
  'projects', 'rewards', 'recognition', 'policies', 'analytics', 'livestream',
  'calendar', 'documents', 'cultureHub', 'pulse', 'badges', 'askAria',
  'leave', 'performance',
];

export const HUBS: Hub[] = [
  { id: 'dashboard', label: 'Dashboard', icon: 'home', items: [
    { label: 'Dashboard', path: '/dashboard' },
  ] },
  { id: 'grow', label: 'Grow', icon: 'grow', items: [
    { label: 'Learning', path: '/learning', moduleName: 'learning' },
    { label: 'Microlearning', path: '/microlearning', moduleName: 'microlearning' },
    { label: 'Games', path: '/gamification', moduleName: 'games' },
  ] },
  { id: 'wellbeing', label: 'Wellbeing', icon: 'wellbeing', items: [
    { label: 'Wellness', path: '/wellness', moduleName: 'wellness' },
    { label: 'Pulse', path: '/pulse', moduleName: 'chat' },
  ] },
  { id: 'performance', label: 'Performance', icon: 'performance', items: [
    { label: 'My Performance', path: '/performance', moduleName: 'performance' },
    { label: 'Team', path: '/manager/performance', moduleName: 'performance', managerOnly: true },
    { label: 'Admin', path: '/admin/performance', moduleName: 'performance', adminOnly: true },
  ] },
  { id: 'rewards', label: 'Rewards', icon: 'rewards', items: [
    { label: 'Rewards', path: '/rewards', moduleName: 'rewards' },
    { label: 'Store', path: '/store', moduleName: 'rewards' },
    { label: 'Recognition', path: '/recognition', moduleName: 'recognition' },
  ] },
  { id: 'workspace', label: 'Workspace', icon: 'workspace', items: [
    { label: 'My Tasks', path: '/tasks', moduleName: 'projects' },
    { label: 'Team Board', path: '/tasks/manager', moduleName: 'projects', managerOnly: true },
    { label: 'Calendar', path: '/calendar', moduleName: 'calendar' },
    { label: 'Documents', path: '/documents', moduleName: 'policies' },
  ] },
  { id: 'connect', label: 'Connect', icon: 'connect', items: [
    { label: 'Announcements', path: '/announcements', moduleName: 'announcements' },
    { label: 'Chat', path: '/chat', moduleName: 'chat' },
    { label: 'Live Stream', path: '/streaming', moduleName: 'livestream' },
    { label: 'Culture', path: '/culture', moduleName: 'cultureHub' },
  ] },
  { id: 'leave', label: 'Leave', icon: 'leave', items: [
    { label: 'My Leave', path: '/leave', moduleName: 'leave' },
  ] },
];

export interface NavPerms {
  enabledModules: string[];
  isManager: boolean;
  isAdmin: boolean;
}

export function itemAllowed(item: HubItem, p: NavPerms): boolean {
  if (item.moduleName && !p.enabledModules.includes(item.moduleName)) return false;
  if (item.managerOnly && !p.isManager) return false;
  if (item.adminOnly && !p.isAdmin) return false;
  return true;
}

export function visibleItems(hub: Hub, p: NavPerms): HubItem[] {
  return hub.items.filter((i) => itemAllowed(i, p));
}

// Hubs the user can actually see (≥1 visible item).
export function visibleHubs(p: NavPerms): Hub[] {
  return HUBS.filter((h) => visibleItems(h, p).length > 0);
}

// The hub that owns the current route (longest path-prefix match wins so
// /admin/performance maps to Performance, not a shorter accidental match).
export function hubForPath(pathname: string): Hub | null {
  let best: Hub | null = null;
  let bestLen = -1;
  for (const hub of HUBS) {
    for (const item of hub.items) {
      if ((pathname === item.path || pathname.startsWith(item.path + '/')) && item.path.length > bestLen) {
        best = hub; bestLen = item.path.length;
      }
    }
  }
  return best;
}

// Landing route for a hub = its first visible item.
export function defaultPath(hub: Hub, p: NavPerms): string | null {
  const items = visibleItems(hub, p);
  return items.length ? items[0].path : null;
}
